import { useTranslation } from 'react-i18next';
import { DropdownContainer, DropdownMessage, dropdownBoxStyle } from './dropdown/dropdown.styles';
import { useActiveItemScroll } from './dropdown/useActiveItemScroll';
import {
  CommitButton,
  NoSelectionRow,
  OptionLabel,
  OptionMarker,
  OptionRow,
  SelectHeader,
} from './SelectionListSelect.styles';

interface SelectionListSelectProps {
  name: string;
  options: string[];
  multiSelect: boolean;
  available: boolean;
  selected: Set<number>;
  activeIndex: number;
  onOptionClick: (index: number) => void;
  onNoSelection: () => void;
  onCommit: () => void;
  onHover: (index: number) => void;
  maxHeight?: number;
  position?: { top: number; left: number };
}

export const SelectionListSelect = ({
  name,
  options,
  multiSelect,
  available,
  selected,
  activeIndex,
  onOptionClick,
  onNoSelection,
  onCommit,
  onHover,
  maxHeight,
  position,
}: SelectionListSelectProps) => {
  const { t } = useTranslation();

  const activeItemRef = useActiveItemScroll<HTMLDivElement>(activeIndex);
  const commitButtonRef = useActiveItemScroll<HTMLButtonElement>(activeIndex);
  const style = dropdownBoxStyle(maxHeight, position);
  const commitActive = multiSelect && activeIndex === options.length;

  return (
    <DropdownContainer role="listbox" aria-label={name} data-ph-no-autocapture style={style}>
      <SelectHeader>{name}</SelectHeader>

      {available ? (
        options.map((option, index) => (
          // onMouseDown (not onClick) + preventDefault keeps the textarea focused through the click.
          <OptionRow
            key={`${index}-${option}`}
            role="option"
            aria-selected={multiSelect ? selected.has(index) : index === activeIndex}
            $active={index === activeIndex}
            ref={index === activeIndex ? activeItemRef : undefined}
            data-selected={multiSelect && selected.has(index) ? 'true' : undefined}
            onMouseDown={(event) => {
              event.preventDefault();
              onOptionClick(index);
            }}
            // onMouseMove, not onMouseEnter: scrolling fires mouseenter on rows under a still cursor.
            onMouseMove={() => onHover(index)}
          >
            {multiSelect ? <OptionMarker>{selected.has(index) ? '×' : ''}</OptionMarker> : null}
            <OptionMarker>{index + 1}</OptionMarker>
            <OptionLabel>{option}</OptionLabel>
          </OptionRow>
        ))
      ) : (
        <DropdownMessage>{t('recording.notes.textBuildingBlocks.notAvailable')}</DropdownMessage>
      )}

      <NoSelectionRow
        onMouseDown={(event) => {
          event.preventDefault();
          onNoSelection();
        }}
      >
        <OptionMarker>0</OptionMarker>
        <OptionLabel>{t('recording.notes.textBuildingBlocks.noSelection')}</OptionLabel>
      </NoSelectionRow>

      {available && multiSelect ? (
        <CommitButton
          $active={commitActive}
          ref={commitActive ? commitButtonRef : undefined}
          onMouseDown={(event) => {
            event.preventDefault();
            onCommit();
          }}
        >
          {t('recording.notes.textBuildingBlocks.apply')}
        </CommitButton>
      ) : null}
    </DropdownContainer>
  );
};
